<script setup lang="ts">
import lodashIsNumber from 'lodash-es/isNumber';
import type { PropType } from 'vue';

import { AuctionState, DirectSaleComputedState, type SaleAssetBusListingItem, SellingMethod } from '~/apiClient';
import { ROUTE_NAMES } from '~/constants/routes';
import { isAuction, isDirectSale, type SaleListingItemEntity } from '~/types/sale.type';

// PROPS
const { sale } = defineProps({
    sale: { type: Object as PropType<SaleListingItemEntity>, required: true },
});

// EVENTS
const emit = defineEmits<{
    navigate: [];
}>();

// COMPOSABLE
const { tt, td, tn, formatUnits } = useTypedI18n();
const { auctionDetail, directSaleDetail } = useA11y();
const { isBundle } = useSaleType(() => sale.type);
const { getPlatformBusClassesLabel } = usePlatformBusClass();

// DATA
const mainAsset = computed<SaleAssetBusListingItem>(() => sale.mainAsset);
const classLabel = computed<string | undefined>(() => {
    const parts = [
        isBundle.value && sale?.assetCount ? tt('asset.vehicleCount', { count: sale?.assetCount }) : null,
        mainAsset.value.class ? getPlatformBusClassesLabel(mainAsset.value.class) : null,
    ].filter(Boolean);

    return parts.length ? parts.join(', ') : undefined;
});
const auctionBidInfo = computed(() => (isAuction(sale) && sale.state === AuctionState.Running ? sale.bidInfo : undefined));
const firstRegistration = computed(() => Date.parse(mainAsset.value.firstRegistration));
const priceColorClass = computed(() => {
    if (isAuction(sale)) {
        return auctionBidInfo.value && (auctionBidInfo.value.maxBidder ? 'text-primary' : 'text-warning-600');
    } else if (isDirectSale(sale)) {
        return sale.computedState === DirectSaleComputedState.NoActiveOrder ? 'text-primary' : 'text-default';
    }
});
const linkProps = computed(() => {
    switch (sale.sellingMethod) {
        case SellingMethod.Auction:
            return { to: { name: ROUTE_NAMES.AUCTION_DETAIL, params: { slug: sale.slug } }, 'aria-label': auctionDetail(sale) };
        case SellingMethod.DirectSale:
            return { to: { name: ROUTE_NAMES.DIRECT_SALE_DETAIL, params: { slug: sale.slug } }, 'aria-label': directSaleDetail(sale) };
    }
});
</script>

<template>
    <NuxtLinkLocale v-bind="linkProps" @click="emit('navigate')">
        <SlpBaseCard>
            <template #header>
                <SlpCardImages :main-asset="mainAsset" />
                <div class="absolute top-3 right-3 left-3 flex justify-between">
                    <div class="flex items-start gap-3">
                        <SaleBadges :sale="sale" icon-only />
                    </div>
                    <SaleFavoritesButton :sale="sale" />
                </div>
                <AuctionReservePriceNotice v-if="isAuction(sale)" class="absolute right-3 bottom-3" :auction="sale" />
                <DslpCardNegotiationAvailableBadge v-if="isDirectSale(sale)" class="absolute right-3 bottom-3" :direct-sale="sale" />
            </template>

            <template v-if="classLabel" #class>{{ classLabel }}</template>

            <template #model>{{ mainAsset.brand.brandValue }} {{ mainAsset.model.modelValue }}</template>

            <template #registration>{{ td(firstRegistration, 'mmyyyyUTC') }}</template>

            <template #kilometer>{{ formatUnits.km(mainAsset.km) }}</template>

            <template #euro-norm>{{ mainAsset.euroNorm ? tt(`asset.euroNorm.${mainAsset.euroNorm}`) : '-' }}</template>

            <template #price>
                <template v-if="isAuction(sale) && lodashIsNumber(sale.currentPrice)">
                    <span :class="priceColorClass">
                        {{ tn(sale.currentPrice, 'currency') }}
                    </span>
                    <AuctionMaxBidderIcon v-if="isAuction(sale)" :auction="sale" />
                </template>
                <span v-else :class="priceColorClass">
                    <template v-if="isDirectSale(sale) && lodashIsNumber(sale.price)">
                        {{ tn(sale.price, 'currency') }}
                    </template>
                    <template v-else> ******* </template>
                </span>
            </template>

            <template #status>
                <AlpCardStatus v-if="isAuction(sale)" :auction="sale" />
                <DslpCardStatus v-else-if="isDirectSale(sale)" :direct-sale="sale" />
            </template>
        </SlpBaseCard>
    </NuxtLinkLocale>
</template>
